import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Divider,
  Switch,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";

export default function EmailExportDialog({
  open,
  emailInput,
  emails,
  emailError,
  exportFormat,
  recurring,
  recurringDay,
  timePicker,
  datePicker,
  isSubmitting,
  hasError,
  onClose,
  onChangeEmailInput,
  onAddEmail,
  onDeleteEmail,
  onChangeExportFormat,
  onToggleRecurring,
  onChangeRecurringDay,
  onSubmit,
}) {
  const { t } = useCustomTranslation("Transactions");

  return (
    <Dialog open={open} onClose={onClose} sx={{ "& .MuiDialog-paper": { minHeight: 70, minWidth: 500 } }}>
      <DialogTitle>{t("transactions_table_export_dialog_title")}</DialogTitle>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          autoComplete="off"
        >
          <LoadingContainer loading={!!isSubmitting} error={!!hasError}>
            <div className="flex flex-1 flex-col space-y-4">
              <TextField
                fullWidth
                placeholder={t("transactions_table_export_email")}
                label={t("transactions_table_export_email")}
                type="email"
                value={emailInput}
                onChange={(e) => onChangeEmailInput(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                InputProps={
                  emailInput.length > 0
                    ? {
                        endAdornment: (
                          <InputAdornment position="end" className="cursor-pointer">
                            <ArrowForwardIcon onClick={onAddEmail} />
                          </InputAdornment>
                        ),
                      }
                    : {}
                }
              />

              <div className="flex flex-wrap gap-2">
                {emails.map((email, index) => (
                  <Chip key={index} label={email} onDelete={() => onDeleteEmail(email)} />
                ))}
              </div>

              {emails.length > 0 && (
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    {t("transactions_table_export_format_text")}
                  </FormLabel>
                  <RadioGroup value={exportFormat} onChange={(e) => onChangeExportFormat(e.target.value)}>
                    <FormControlLabel value="csv" control={<Radio />} label="CSV" />
                    <FormControlLabel value="xlsx" control={<Radio />} label="XLSX" />
                  </RadioGroup>
                </FormControl>
              )}

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={!!recurring}
                    onChange={(e) => onToggleRecurring(e.target.checked)}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={t("transactions_table_export_is_recurring")}
                disabled={!emails.length}
              />

              {recurring && (
                <div className="mt-2 space-y-4">
                  <FormControl fullWidth>
                    <TextField
                      label={t("transactions_table_export_day_of_month")}
                      type="number"
                      required
                      value={recurringDay}
                      onChange={(e) => onChangeRecurringDay(e.target.value)}
                      InputProps={{ inputProps: { min: 1, max: 31 } }}
                      helperText={t("transactions_table_export_day_of_month_helper_text")}
                    />
                  </FormControl>
                  {timePicker}
                  {datePicker}
                </div>
              )}
            </div>
          </LoadingContainer>

          <DialogActions>
            <Button variant="outlined" color="error" onClick={onClose}>
              {t("transactions_table_export_cancel_button")}
            </Button>
            <Button variant="contained" type="submit" disabled={!emails.length || !!isSubmitting}>
              {t("transactions_table_export_send_button")}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}
